import type { ReactNode } from "react";
import styled from "styled-components";

export type IconChipSize = "s" | "m" | "l";

export interface IconChipProps {
  /** Icon element (e.g. a phosphor icon). Rendered in `color`; the chip
   *  controls its size via `size`, so pass it without a size of its own. */
  icon: ReactNode;
  /** Accent color as a 6-digit hex (e.g. "#14B8A6"). The background is tinted to
   *  ~12% of it and the icon renders in the full color. */
  color: string;
  /** Chip size. Defaults to "m". */
  size?: IconChipSize;
  className?: string;
}

const sizeConfig: Record<
  IconChipSize,
  { box: number; radius: number; icon: number }
> = {
  s: { box: 28, radius: 8, icon: 16 },
  m: { box: 40, radius: 10, icon: 22 },
  l: { box: 44, radius: 12, icon: 22 },
};

const StyledIconChip = styled.span<{ $color: string; $size: IconChipSize }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: ${({ $size }) => sizeConfig[$size].box}px;
  height: ${({ $size }) => sizeConfig[$size].box}px;
  border-radius: ${({ $size }) => sizeConfig[$size].radius}px;
  color: ${({ $color }) => $color};
  background: ${({ $color }) => `${$color}1f`};

  svg {
    width: ${({ $size }) => sizeConfig[$size].icon}px;
    height: ${({ $size }) => sizeConfig[$size].icon}px;
  }
`;

/**
 * IconChip — a small rounded, color-tinted square holding an icon. Gives a
 * category/add-on a recognizable colored glyph in pickers, list rows and card
 * headers. Pass a hex `color`; the chip tints its background to ~12% of it and
 * renders the icon in the full color.
 *
 * @example
 * ```tsx
 * import { IconChip } from '@libertify/ui';
 * import { VideoCameraIcon } from '@phosphor-icons/react';
 *
 * <IconChip color="#14B8A6" icon={<VideoCameraIcon weight="duotone" />} size="l" />
 * ```
 */
export const IconChip = ({
  icon,
  color,
  size = "m",
  className,
}: IconChipProps) => (
  <StyledIconChip $color={color} $size={size} className={className}>
    {icon}
  </StyledIconChip>
);

export default IconChip;
